import { useEffect, useMemo, useState } from 'react'
import type { ReservationWidget as ReservationWidgetModel } from '@sevenrooms/domain'
import { ReservationWidget } from '@sevenrooms/domain/constants'
import { useLocales } from '@sevenrooms/locales'
import { DateOnly } from '@sevenrooms/timepiece'
import { Box } from '@sevenrooms/ui-kit/layout'
import {
  type AvailabilityTimeWithUpSellCost,
  useReservationsRoute,
  useSingleDayAvailability,
  useVenue,
  useWidgetLanguage,
  useWidgetSettings,
} from '../../hooks'
import { type ExperienceWithAvailability, useExperiencesWithAvailability } from '../../hooks/useAvailability'
import { reservationWidgetMessages } from '../../reservationWidgetMessages'
import {
  OtherDaysReservationAvailability,
  OtherVenuesAvailability,
  type OtherDaysReservationAvailabilityProps,
} from '../AlternativeAvailability'
import { PrimaryAvailability } from '../PrimaryAvailability'
import { SkeletonBox } from '../SkeletonBox'
import type { OtherVenueInfo } from '@/domain'

export interface AvailabilityResultsProps {
  venuesInfo?: ReservationWidgetModel.VenueInfo
  startDate: Date
  haloTimeIntervalMinutes: number
  startTime: string
  partySize: number
  duration?: number
  onAvailabilitySelect: OtherDaysReservationAvailabilityProps['onTimeSlotClick']
  handleOnDateClick: OtherDaysReservationAvailabilityProps['onDateClick']
  onPrivateEventsExploreClick?: () => void
  onPrivateEventsTimeSlotClick?: (
    slot: AvailabilityTimeWithUpSellCost,
    experience: ExperienceWithAvailability,
    venue?: OtherVenueInfo
  ) => void
  onPrivateEventsMoreDetailsClick?: (experience: ExperienceWithAvailability) => void
  onClearTimeFilter: () => void
  isDayBlocked: (day: Date) => boolean
  clientId?: string
  showPrivateEvents: boolean
  actualId?: string
}

export function AvailabilityResults({
  venuesInfo,
  startDate,
  startTime,
  partySize,
  haloTimeIntervalMinutes,
  duration,
  onAvailabilitySelect,
  handleOnDateClick,
  onClearTimeFilter,
  isDayBlocked,
  clientId,
  onPrivateEventsExploreClick,
  onPrivateEventsTimeSlotClick,
  onPrivateEventsMoreDetailsClick,
  showPrivateEvents,
  actualId,
}: AvailabilityResultsProps) {
  const { name, startOfDayTime, urlKey, id: venueId, currencyCode, multiVenueReservationEnabled } = useVenue()
  const { defaultSearchTime, enablePrivateEventsBanner } = useWidgetSettings()
  const { channel, primaryVenueId } = useReservationsRoute()
  const { formatMessage } = useLocales()
  const { selectedLanguage } = useWidgetLanguage()
  const [isOtherDatesSectionCollapsed, setIsOtherDatesSectionCollapsed] = useState(true)
  const [otherVenuesCollapsed, setOtherVenuesCollapsed] = useState(true)
  const dateISO = DateOnly.fromDate(startDate).toIso()

  const {
    isFetching: isAvailabilityFetching,
    isClosed,
    isBlackout,
    data,
    errorCode,
  } = useSingleDayAvailability({
    venueId,
    venue: urlKey,
    startDate: dateISO,
    startTime,
    partySize,
    pickedDuration: duration,
    selectedLangCode: selectedLanguage,
    haloTimeIntervalMinutes,
    channel: channel || ReservationWidget.SevenRoomsWidgetChannel,
    haloSizeInterval: ReservationWidget.WholeDayHaloSizeInterval,
    timeSlot: defaultSearchTime,
    startOfDayTime,
    skipRequests: false,
    clientId,
    primaryVenueId,
    excludePdr: true,
    actualId,
  })

  const { data: experiences, isFetching: isFetchingExperiences } = useExperiencesWithAvailability({
    venue: venueId,
    partySize,
    startDate: startTime ? DateOnly.fromDate(startDate).toIso() : undefined,
    clientId,
    selectedLangCode: selectedLanguage,
    skipRequests: !showPrivateEvents,
    startTime,
    haloTimeIntervalMinutes,
    startOfDayTime,
    experienceType: 'PDR',
  })

  const experiencesToDisplay = useMemo(() => experiences?.filter(experience => !!experience.reservationTimesInHalo?.length), [experiences])
  const showPrivateEventsBanner = showPrivateEvents && enablePrivateEventsBanner && !isFetchingExperiences && !experiencesToDisplay?.length

  useEffect(() => {
    // Should collapse when the filter values changes.
    setIsOtherDatesSectionCollapsed(true)
    setOtherVenuesCollapsed(true)
  }, [startDate, haloTimeIntervalMinutes, startTime, partySize])

  useEffect(() => {
    if (data?.reservationTimesInHalo.length === 0) {
      // Should collapse and expand to refresh data
      setIsOtherDatesSectionCollapsed(true)
      setTimeout(() => {
        setIsOtherDatesSectionCollapsed(false)
      })
    }
  }, [data])

  return (
    <>
      <PrimaryAvailability
        onClearTimeFilter={onClearTimeFilter}
        partySize={partySize}
        startDate={DateOnly.fromDate(startDate).toIso()}
        isDayBlocked={isDayBlocked}
        onClick={onAvailabilitySelect}
        venueCurrencyCode={currencyCode}
        reservationTimesInHalo={data?.reservationTimesInHalo || []}
        requestTimeRange={data?.requestTimeRange || []}
        reservationTimes={data?.reservationTimes || []}
        experiences={experiencesToDisplay}
        isAvailabilityFetching={isAvailabilityFetching}
        isClosed={isClosed}
        isBlackout={isBlackout}
        showPrivateEventsBanner={showPrivateEventsBanner}
        onPrivateEventsExploreClick={onPrivateEventsExploreClick}
        onPrivateEventsTimeSlotClick={onPrivateEventsTimeSlotClick}
        onPrivateEventsMoreDetailsClick={onPrivateEventsMoreDetailsClick}
        errorCode={errorCode}
      />
      {multiVenueReservationEnabled &&
        venuesInfo &&
        (isAvailabilityFetching ? (
          <SkeletonBox height="58px" mt="lm" />
        ) : (
          <OtherVenuesAvailability
            partySize={partySize}
            startTime={startTime}
            startDate={DateOnly.fromDate(startDate).toIso()}
            defaultSearchTime={defaultSearchTime}
            duration={duration}
            haloTimeIntervalMinutes={haloTimeIntervalMinutes}
            clientId={clientId}
            otherVenues={venuesInfo.venues.filter(v => v.urlKey !== urlKey)}
            selectedLangCode={selectedLanguage}
            startOfDayTime={startOfDayTime}
            currencyCode={currencyCode}
            title={formatMessage(reservationWidgetMessages.resWidgetOtherLocationsLabel)}
            onTimeSlotClick={(slot: AvailabilityTimeWithUpSellCost, venue: OtherVenueInfo) =>
              onAvailabilitySelect?.(slot, undefined, venue)
            }
            collapsed={otherVenuesCollapsed}
            setCollapsed={setOtherVenuesCollapsed}
            onPrivateEventsMoreDetailsClick={onPrivateEventsMoreDetailsClick}
            onPrivateEventsTimeSlotClick={onPrivateEventsTimeSlotClick}
            showPrivateEvents={showPrivateEvents}
          />
        ))}
      <Box pt="lm">
        {isAvailabilityFetching ? (
          <SkeletonBox height="58px" />
        ) : (
          <OtherDaysReservationAvailability
            partySize={partySize}
            startTime={startTime}
            startDate={startDate}
            haloTimeIntervalMinutes={haloTimeIntervalMinutes}
            duration={duration}
            clientId={clientId}
            noAvailabilityMessage={formatMessage(reservationWidgetMessages.resWidgetNoAdditionalAvail)}
            onDateClick={handleOnDateClick}
            onTimeSlotClick={onAvailabilitySelect}
            title={formatMessage(reservationWidgetMessages.resWidgetOtherDatesLabel, { venue: name })}
            venueCurrencyCode={currencyCode}
            isCollapsed={isOtherDatesSectionCollapsed}
            onToggleCollapse={(isCollapsed: boolean) => {
              setIsOtherDatesSectionCollapsed(isCollapsed)
            }}
          />
        )}
      </Box>
    </>
  )
}
